/**
 * @name DialogOverlays
 * @description Controls global overlay navigation elements. Closely related to the dialog module.
 */

class DialogOverlays {
  /**
   * @param {HTMLElement} overlayNode - The element groups the overlay elements.
   */
  constructor(overlayNode) {
    this.overlayNode = overlayNode;
    this.selectElements();
    this.registerEventBus();

    this.boundHandleBackdropClick = this.handleBackdropClick.bind(this);
  }

  selectElements() {
    this.overlayToggles = Array.from(document.querySelectorAll('[data-overlay-toggle]'));

    // Track attached handlers loosely so each toggle only gets its listener once
    if (!this._overlayToggleHandlers) this._overlayToggleHandlers = new WeakMap();

    this.overlayToggles.forEach((overlayToggle) => {
      if (this._overlayToggleHandlers.has(overlayToggle)) return;

      const overlayId = overlayToggle.dataset.overlayToggle;
      const handler = () => {
        this.toggleOverlay(overlayId);
      };

      overlayToggle.addEventListener('click', handler);
      this._overlayToggleHandlers.set(overlayToggle, handler);
    });
  }

  /**
   * Trigger toggle for the overlay item.
   * If the overlay item is already open, it will be closed.
   * If the overlay item is closed, it will be opened.
   * If no overlayId is provided, all overlays will be closed.
   *
   * @param {string|null} overlayId
   */
  toggleOverlay(overlayId) {
    const overlayItemNode = Array.from(this.overlayNode.querySelectorAll('[data-overlay-item]')).find((item) => item.dataset.overlayItem === overlayId);

    if (!overlayItemNode) {
      return;
    }

    if (overlayItemNode.open) {
      this.closeOverlay(overlayItemNode);
    } else {
      this.openOverlay(overlayItemNode);
    }
  }

  /**
   * Open an overlay by its ID.
   * @param {string} overlayId
   * @returns if overlay ID was not found.
   */
  openOverlayById(overlayId) {
    const overlayItemNode = Array.from(this.overlayNode.querySelectorAll('[data-overlay-item]')).find((item) => item.dataset.overlayItem === overlayId);
    if (overlayItemNode) {
      this.openOverlay(overlayItemNode);
      return;
    }

    const overlayTemplateNode = this.overlayNode.querySelector(`template[data-overlay-item-template="${overlayId}"]`);
    if (!overlayTemplateNode) {
      return;
    }
    
    const clonedOverlay = overlayTemplateNode.content.firstElementChild.cloneNode(true);
    overlayTemplateNode.parentNode.appendChild(clonedOverlay);
    this.selectElements();

    this.openOverlay(clonedOverlay);
  }

  /**
   * Close an overlay by its ID.
   * If the overlay ID is not found, it will close all overlays.
   * @param {string} overlayId
   * @returns
   */
  closeOverlayById(overlayId) {
    const overlayItemNode = Array.from(this.overlayNode.querySelectorAll('[data-overlay-item]')).find((item) => item.dataset.overlayItem === overlayId);

    if (!overlayItemNode) {
      return;
    }
    this.closeOverlay(overlayItemNode);
  }

  /**
   * Trigger open animation for the overlay item.
   * @param {HTMLDialogElement} overlayItemNode
   */
  openOverlay(overlayItemNode) {
    if (!overlayItemNode || overlayItemNode.open) {
      return;
    }

    // Set timestamp to track opening order
    overlayItemNode.dataset.openedAt = Date.now();

    overlayItemNode.showModal();

    window.requestAnimationFrame(() => {
      overlayItemNode.classList.add('dialog--open');
      document.body.classList.add('er-scroll--lock');
    });

    overlayItemNode.addEventListener('click', this.boundHandleBackdropClick);
  }

  /**
   * Trigger close animation for the overlay item.
   * @param {HTMLDialogElement} overlayItemNode
   */
  closeOverlay(overlayItemNode) {
    if (!overlayItemNode || !overlayItemNode.open) {
      return;
    }

    DialogOverlays._performDialogClose(overlayItemNode, this.overlayNode, this.boundHandleBackdropClick);
  }

  /**
   * @description Closes a dialog by ID
   */
  static closeDialogById(dialogId) {
    const dialogElement = document.querySelector(`[data-overlay-item="${dialogId}"]`);

    if (!dialogElement || !dialogElement.open) {
      return;
    }

    const overlayNode = document.querySelector('[data-overlay]');
    DialogOverlays._performDialogClose(dialogElement, overlayNode);
  }

  /**
   * @description Shared dialog close implementation with animations and cleanup
   * @param {HTMLDialogElement} dialogElement - The dialog element to close
   * @param {HTMLElement} overlayNode - The overlay container node (for focus management)
   * @param {Function} backdropClickHandler - Optional backdrop click handler to remove
   */
  static _performDialogClose(dialogElement, overlayNode = null, backdropClickHandler = null) {
    const closeOverlayAfterTransitioned = () => {
      
      dialogElement.close();

      if (document.querySelectorAll('dialog[open]').length === 0) {
        document.body.classList.remove('er-scroll--lock');

        // Refocus to skip link for system dialogs (only if no URL hash is present)
        if (
          !window.location.hash &&
          overlayNode &&
          (dialogElement.dataset.overlayItem === overlayNode.dataset.overlayCookieMessage ||
            dialogElement.dataset.overlayItem === overlayNode.dataset.overlayTuMessage ||
            dialogElement.dataset.overlayItem === overlayNode.dataset.overlayPhaseoutMessage)
        ) {
          const skipLinkInit = document.querySelector('#skip-link-init');
          if (skipLinkInit) {
            skipLinkInit.focus();
          }
        }
      }

      // Clean up event listeners
      if (backdropClickHandler) {
        dialogElement.removeEventListener('click', backdropClickHandler);
      }
      dialogElement.removeEventListener('transitionend', closeOverlayAfterTransitioned);
    };

    dialogElement.addEventListener('transitionend', closeOverlayAfterTransitioned);
    dialogElement.classList.remove('dialog--open');
  }

  handleBackdropClick(event) {
    if (event.target === event.currentTarget) {
      this.closeOverlay(event.currentTarget);
    }
  }

  registerEventBus() {
    const overlayCookieId = this.overlayNode.dataset.overlayCookieMessage;
    const overlayLangId = this.overlayNode.dataset.overlayLang;
    const overlaySearchId = this.overlayNode.dataset.overlaySearch;
    const overlayTuId = this.overlayNode.dataset.overlayTuMessage;
    const phaseoutMessageId = this.overlayNode.dataset.overlayPhaseoutMessage;

    document.addEventListener('showCookieMessage', () => this.openOverlayById(overlayCookieId));
    document.addEventListener('hideCookieMessage', () => this.closeOverlayById(overlayCookieId));
    document.addEventListener('showLangSelect', () => this.openOverlayById(overlayLangId));
    document.addEventListener('hideLangSelect', () => this.closeOverlayById(overlayLangId));
    document.addEventListener('showSearch', () => this.openOverlayById(overlaySearchId));
    document.addEventListener('hideSearch', () => this.closeOverlayById(overlaySearchId));
    document.addEventListener('showTUMessage', () => this.openOverlayById(overlayTuId));
    document.addEventListener('hideTUMessage', () => this.closeOverlayById(overlayTuId));
    document.addEventListener('showPhaseoutMessage', () => this.openOverlayById(phaseoutMessageId));
    document.addEventListener('hidePhaseoutMessage', () => this.closeOverlayById(phaseoutMessageId));
  }

  /**
   * @description Initializes global ESC handler for all dialogs
   */
  static initGlobalEscHandler() {
    document.addEventListener('keydown', function (event) {
      
      // handle ESC key only
      if (event.key !== 'Escape') {
        return
      }

      // check if any popover is open
      if (window.PopoverController.isAnyPopoverOpen()) {
        return;
      }

      // ensure at least one open dialog
      const openDialogs = [...document.querySelectorAll('dialog[open]')];
      if (openDialogs.length === 0) return;

      // check if any of the open dialogs is an overlay or specsheet dialog
      const hasOverlayDialog = openDialogs.some(dialog =>
        dialog.hasAttribute('data-overlay-item') || dialog === window.Specsheets?.dialogWrapperElement
      );
      if (!hasOverlayDialog) return;

      // Find topmost dialog using timestamps
      const topmostDialog = openDialogs.reduce((latest, current) => {
        const latestTime = parseInt(latest.dataset.openedAt || 0);
        const currentTime = parseInt(current.dataset.openedAt || 0);
        return currentTime > latestTime ? current : latest;
      });

      // Prevent default browser behavior and other handlers
      event.preventDefault();
      event.stopImmediatePropagation();

      // Close the topmost dialog directly
      if (topmostDialog === window.Specsheets?.dialogWrapperElement) {
        // Specsheet dialog
        window.Specsheets.close();
      } else {
        // DialogOverlays dialog - close using static method
        DialogOverlays.closeDialogById(topmostDialog.dataset.overlayItem);
      }
    });
  }

  /**
   * @description Initializes all elements with the data-overlay attribute
   */
  static initOverlays() {
    const triggerOverlayElements = document.querySelectorAll('[data-overlay]');

    triggerOverlayElements.forEach((element) => {
      new DialogOverlays(element);
    });

    // Initialize global ESC handler
    this.initGlobalEscHandler();
  }
}
